import React, { useEffect, useState } from "react";
import {
  fetchBooks,
  deleteBook,
  createBook,
} from "../../../Api/Speakers/Books";
import { useTranslation } from "react-i18next";
import AOS from "aos";
import "aos/dist/aos.css";
import AddBookModal from "./AddBookModal";
import EditBookModal from "./EditBookModal";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const BooksManagement = () => {
  const { t, i18n } = useTranslation();
  const languageDirection = i18n.language === "ar" ? "rtl" : "ltr";
  const [showAddModal, setShowAddModal] = useState(false);

  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingBook, setEditingBook] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  const [bookData, setBookData] = useState({
    arabicTitle: "",
    englishTitle: "",
    link: "",
    arabicDescription: "",
    englishDescription: "",
    coverImage: null,
  });

  useEffect(() => {
    AOS.init({ duration: 1000 });
    loadBooks();
  }, []);

  const loadBooks = async () => {
    try {
      const response = await fetchBooks({ page: 1, pageSize: 10 });
      console.log("Books response", response);
      setBooks(response?.items || response || []);
    } catch (error) {
      console.error("Error fetching books:", error);
    }
  };

  const handleFileChange = (e) => {
    setBookData({ ...bookData, coverImage: e.target.files[0] });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    toast.dismiss();

    const formData = new FormData();
    formData.append("coverPagePath", bookData.coverImage);
    Object.entries(bookData).forEach(([key, value]) => {
      if (key !== "coverImage") {
        formData.append(key, value);
      }
    });

    try {
      setLoading(true);
      await createBook(formData);
      toast.success(t("bookCreatedSuccess"));
      await loadBooks();
      setShowAddModal(false);
    } catch (error) {
      const errorMessage =
        error.detail || error.title || t("AddBook.bookCreatedError");
      toast.error(errorMessage);
    } finally {
      setLoading(false);
      setBookData({
        arabicTitle: "",
        englishTitle: "",
        link: "",
        arabicDescription: "",
        englishDescription: "",
        coverImage: null,
      });
    }
  };

  const handleDelete = async (bookId) => {
    if (!window.confirm(t("confirm_delete"))) return;
    try {
      await deleteBook(bookId);
      toast.success(t("book_deleted_successfully"));
      await loadBooks();
    } catch (error) {
      toast.error(t("book_delete_error"));
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3 data-translate="books_management">{t("books_management")}</h3>
        <button
          className="btn btn-primary"
          id="openAddBookPanel"
          onClick={() => setShowAddModal(true)}
          dir={languageDirection}
        >
          <i className="fas fa-plus me-2"></i>
          {t("add_new")}
        </button>
      </div>

      <AddBookModal
        show={showAddModal}
        onHide={() => setShowAddModal(false)}
        bookData={bookData}
        setBookData={setBookData}
        handleFileChange={handleFileChange}
        handleSubmit={handleSubmit}
        loading={loading}
      />

      {editingBook && (
        <EditBookModal
          show={showEditModal}
          onHide={() => setShowEditModal(false)}
          book={editingBook}
          reloadBooks={loadBooks}
          t={t}
        />
      )}

      <div className="table-responsive">
        <table className="table table-hover">
          <thead className="table-light">
            <tr>
              <th width="70">#</th>
              <th width="100" data-translate="cover_image">
                {t("cover_image")}
              </th>
              <th data-translate="book_title_ar">{t("book_title_ar")}</th>
              <th data-translate="book_title_en">{t("book_title_en")}</th>
              {/* <th width="100" data-translate="date">
                {t("date")}
              </th> */}
              <th width="150" data-translate="actions">
                {t("actions")}
              </th>
            </tr>
          </thead>
          <tbody>
            {books.length > 0 ? (
              books.map((book, index) => (
                <tr key={book.id}>
                  <td>{index + 1}</td>
                  <td>
                    {book.coverPagePath ? (
                      <img
                        src={book.coverPagePath}
                        alt="cover"
                        style={{
                          width: "50px",
                          height: "70px",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <span className="text-muted">لا يوجد</span>
                    )}
                  </td>
                  <td>{book.arabicTitle}</td>
                  <td>{book.englishTitle}</td>
                  {/* <td>{new Date(book.createdAt).toLocaleDateString()}</td> */}
                  <td>
                    <button
                      className="btn btn-sm btn-outline-primary me-2"
                      onClick={() => {
                        setEditingBook(book);
                        setShowEditModal(true);
                      }}
                    >
                      <i className="fas fa-edit"></i>
                    </button>
                    <button
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => handleDelete(book.id)}
                    >
                      <i className="fas fa-trash"></i>
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr className="no-data-message">
                <td colSpan="6" className="text-center py-4">
                  <div className="empty-state">
                    <i className="fas fa-book fa-3x mb-3 text-muted"></i>
                    <h5 className="text-muted">{t("no_books_message")}</h5>
                    <p className="text-muted small">{t("press_add_new")}</p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default BooksManagement;
